import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { useModelImpersonation } from "@/hooks/useModelImpersonation";
import { useAuth } from "@/contexts/AuthContext";
import { PipelineStage } from "@/components/pipeline/PipelineStage";
import { ModelLevelBadge } from "@/components/pipeline/ModelLevelBadge";
import { CalendarButton } from "@/components/home/CalendarButton";
import { ProfileIncompleteDialog } from "@/components/home/ProfileIncompleteDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import lojaMaisonHero from "@/assets/loja-maison-hero.jpg";
import type { ModelLevel } from "@/lib/supabase";

const pipelineStages = [
  { stage: 1, title: "CONTRATO", description: "Preencha seus dados e assine o contrato digital", route: "/contrato" },
  { stage: 2, title: "CRIAÇÃO DE CONTAS", description: "Configure suas contas Maloum e 4Based", route: "/criacao-contas" },
  { stage: 3, title: "VERIFICAÇÃO", description: "Verifique suas contas nas plataformas", route: "/verificacao" },
  { stage: 4, title: "ENVIO DE CONTEÚDO", description: "Envie 100 fotos/vídeos e 2 SETs", route: "/envio-conteudo" },
  { stage: 5, title: "INÍCIO DE POSTAGENS", description: "Ativação das postagens nas plataformas", route: "/inicio-postagens" },
  { stage: 6, title: "PRIMEIRA VENDA", description: "Parabéns pela sua primeira venda!", route: "/primeira-venda" },
];

const STAGE_ORDER = [
  "contract",
  "account_creation",
  "verification",
  "content_start",
  "posting_start",
  "first_sale",
] as const;

export default function Index() {
  const { user, profile, isLoading, isProfileComplete, userRole, userRoleData } = useAuth();
  const navigate = useNavigate();
  const { isImpersonating, impersonatedProfile, isLoading: impLoading } = useModelImpersonation();
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [isPipelineOpen, setIsPipelineOpen] = useState(false);
  const [showProfileIncompleteDialog, setShowProfileIncompleteDialog] = useState(false);
  const [pipeline, setPipeline] = useState<{
    current_stage: string | null;
    contract_signed: boolean | null;
    maloum_posting_active: boolean | null;
    fourbased_posting_active: boolean | null;
    first_sale_date: string | null;
    first_sale_amount: number | null;
  } | null>(null);
  const [earnings, setEarnings] = useState<{
    current_level: ModelLevel;
    model_share: number;
    maloum_total: number;
    fourbased_chat_total: number;
    feed_sales_total: number;
    show_maloum: boolean;
    show_fourbased: boolean;
  } | null>(null);

  // Use impersonated profile when available, otherwise use the real one
  const effectiveProfile = isImpersonating && impersonatedProfile ? impersonatedProfile : profile;

  // Check if all 5 fixed questions are answered
  const [areQuestionsAnswered, setAreQuestionsAnswered] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  useEffect(() => {
    const checkQuestions = async () => {
      if (!effectiveProfile?.id) {
        setQuestionsLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("internal_answer_1, internal_answer_2, internal_answer_3, internal_answer_4, internal_answer_5")
          .eq("id", effectiveProfile.id)
          .single();
        
        if (error) throw error;
        
        // Check if all 5 answers are filled
        const allAnswered = !!(
          data?.internal_answer_1?.trim() &&
          data?.internal_answer_2?.trim() &&
          data?.internal_answer_3?.trim() &&
          data?.internal_answer_4?.trim() &&
          data?.internal_answer_5?.trim()
        );
        
        setAreQuestionsAnswered(allAnswered);
      } catch (err) {
        console.error("Error checking questions:", err);
        setAreQuestionsAnswered(false);
      } finally {
        setQuestionsLoading(false);
      }
    };
    
    checkQuestions();
  }, [effectiveProfile?.id]);

  // Liberação do pipeline deve seguir o critério oficial do sistema (is_profile_complete)
  // + perguntas obrigatórias.
  // Não usamos validação manual (ex: endereço) aqui, pois isso pode bloquear modelos
  // que já foram marcadas como perfil completo no backend.
  const canStartPipeline = isImpersonating || (isProfileComplete && areQuestionsAnswered);

  useEffect(() => {
    if (isImpersonating) return; // Don't redirect when impersonating
    // Only redirect to auth if we're done loading AND there's no user
    if (!isLoading && !user) {
      navigate("/auth");
    } else if (!isLoading && user && profile && !isProfileComplete) {
      navigate("/complete-profile");
    } else if (!isLoading && user && userRole === "admin") {
      navigate("/admin");
    } else if (!isLoading && user && userRole === "employee") {
      // Check if user is a recruiter - they have their own interface
      if (userRoleData?.employee_type === "recrutador") {
        navigate("/recruiter");
      } else if (userRoleData?.employee_type === "recruter_admin") {
        navigate("/recruiter-admin");
      } else if (userRoleData?.employee_type === "va_postagem") {
        navigate("/va-postagem");
      } else if (userRoleData?.employee_type === "ai_creator") {
        navigate("/ai-creator");
      } else if (userRoleData?.employee_type === "social_media_va") {
        navigate("/social-media-va");
      } else {
        navigate("/employee");
      }
    }
  }, [user, profile, isLoading, isProfileComplete, userRole, userRoleData, navigate, isImpersonating]);


  useEffect(() => {
    const run = async () => {
      if (!user || !effectiveProfile) return;
      setPipelineLoading(true);
      try {
        // Fetch pipeline and earnings in parallel
        const [pipelineResult, earningsResult] = await Promise.all([
          supabase
            .from("model_pipeline")
            .select("current_stage, contract_signed, maloum_posting_active, fourbased_posting_active, first_sale_date, first_sale_amount")
            .eq("profile_id", effectiveProfile.id)
            .maybeSingle(),
          supabase
            .from("model_earnings")
            .select("current_level, model_share, maloum_total, fourbased_chat_total, feed_sales_total, show_maloum, show_fourbased")
            .eq("profile_id", effectiveProfile.id)
            .maybeSingle()
        ]);

        if (pipelineResult.error) throw pipelineResult.error;
        setPipeline(pipelineResult.data ?? null);
        
        if (earningsResult.data) {
          setEarnings({
            current_level: earningsResult.data.current_level as ModelLevel,
            model_share: earningsResult.data.model_share ?? 0,
            maloum_total: earningsResult.data.maloum_total ?? 0,
            fourbased_chat_total: earningsResult.data.fourbased_chat_total ?? 0,
            feed_sales_total: earningsResult.data.feed_sales_total ?? 0,
            show_maloum: earningsResult.data.show_maloum ?? true,
            show_fourbased: earningsResult.data.show_fourbased ?? true,
          });
        }
      } catch (e) {
        console.error("[Index] Falha ao carregar dados", e);
        setPipeline(null);
      } finally {
        setPipelineLoading(false);
      }
    };

    run();
  }, [user, effectiveProfile]);

  const currentStageIndex = useMemo(() => {
    const stageKey = pipeline?.current_stage;

    // Se o admin marcou contrato assinado mas por algum motivo o current_stage ainda está em "contract",
    // liberamos o próximo passo mesmo assim.
    const effectiveStageKey =
      pipeline?.contract_signed && stageKey === "contract" ? "account_creation" : stageKey;

    const idx = effectiveStageKey ? STAGE_ORDER.indexOf(effectiveStageKey as any) : -1;
    return idx >= 0 ? idx : 0;
  }, [pipeline]);

  // Calculate posting stage status (for stage 5)
  const postingStatus = useMemo(() => {
    const maloum = pipeline?.maloum_posting_active ?? false;
    const fourbased = pipeline?.fourbased_posting_active ?? false;
    
    if (maloum && fourbased) {
      return { count: 2, isPartial: false, isComplete: true };
    } else if (maloum || fourbased) {
      return { count: 1, isPartial: true, isComplete: false };
    }
    return { count: 0, isPartial: false, isComplete: false };
  }, [pipeline]);

  // Calculate first sale status (for stage 6)
  const firstSaleCompleted = useMemo(() => {
    return !!(pipeline?.first_sale_date && pipeline?.first_sale_amount);
  }, [pipeline]);

  // Calculate completed stages count
  const completedStagesCount = useMemo(() => {
    let count = 0;
    
    // Stage 1: Contract
    if (currentStageIndex > 0) count++;
    
    // Stage 2: Account Creation
    if (currentStageIndex > 1) count++;
    
    // Stage 3: Verification
    if (currentStageIndex > 2) count++;
    
    // Stage 4: Content Start
    if (currentStageIndex > 3) count++;
    
    // Stage 5: Posting Start
    if (postingStatus.isComplete) count++;
    
    // Stage 6: First Sale
    if (firstSaleCompleted) count++;
    
    return count;
  }, [currentStageIndex, postingStatus.isComplete, firstSaleCompleted]);

  const remainingStages = 6 - completedStagesCount;

  if (isLoading || pipelineLoading || questionsLoading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-8 pb-24 lg:pb-8">
        {/* Level Badge with Profile */}
        <ModelLevelBadge 
          level={earnings?.current_level ?? "bronze"} 
          totalEarned={earnings?.model_share ?? 0} 
          maloumTotal={earnings?.show_maloum ? (earnings?.maloum_total ?? 0) : 0}
          fourbasedChatTotal={earnings?.show_fourbased ? (earnings?.fourbased_chat_total ?? 0) : 0}
          feedSalesTotal={earnings?.show_fourbased ? (earnings?.feed_sales_total ?? 0) : 0}
          avatarUrl={effectiveProfile?.avatar_url}
          username={effectiveProfile?.german_artistic_name || effectiveProfile?.username}
        />

        {/* Calendar Section - Show ABOVE pipeline when model is verified */}
        {remainingStages === 0 && effectiveProfile?.id && (
          <div className="space-y-2">
            <h2 className="font-display text-lg tracking-wide text-muted-foreground">
              📅 PRÓXIMA ENTREGA
            </h2>
            <CalendarButton profileId={effectiveProfile.id} />
          </div>
        )}

        {/* Pipeline Collapsible */}
        <div className="space-y-3">
          {/* Description - only show when not verified */}
          {remainingStages > 0 && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              Complete todas as etapas do cadastro para se tornar uma modelo verificada. 
              Isso inclui assinar o contrato, criar suas contas nas plataformas, verificar seu perfil, 
              enviar seu conteúdo inicial e realizar sua primeira venda.
            </p>
          )}

          <Collapsible 
            open={isPipelineOpen} 
            onOpenChange={(open) => {
              // If trying to open and profile/questions incomplete, show dialog instead
              if (open && !canStartPipeline) {
                setShowProfileIncompleteDialog(true);
                return;
              }
              setIsPipelineOpen(open);
            }}
          >
            <CollapsibleTrigger asChild>
              <button 
                className="w-full flex items-center justify-between p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-r from-primary to-primary/80 text-primary-foreground"
              >
                <div className="flex items-center gap-3">
                  {remainingStages === 0 && (
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-display text-xl tracking-wide">
                      {remainingStages === 0 ? "MODELO VERIFICADA" : "CADASTRO DO MODELO"}
                    </span>
                    <span className="text-sm opacity-90">
                      {remainingStages === 0 
                        ? "✓ Todas as etapas concluídas!" 
                        : `${remainingStages} etapa${remainingStages > 1 ? 's' : ''} restante${remainingStages > 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                </div>
                <ChevronDown 
                  className={cn(
                    "w-6 h-6 transition-transform duration-300",
                    isPipelineOpen && "rotate-180"
                  )} 
                />
              </button>
            </CollapsibleTrigger>
          
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="pt-4 space-y-4">
              {pipelineStages.map((stage, index) => {
                // Special logic for stage 5 (posting_start)
                const isStage5 = index === 4;
                const isPartialStage5 = isStage5 && postingStatus.isPartial;
                const isCompleteStage5 = isStage5 && postingStatus.isComplete;
                
                // Special logic for stage 6 (first_sale)
                const isStage6 = index === 5;
                const isCompleteStage6 = isStage6 && firstSaleCompleted;
                
                // Stage 5 is unlocked if we're at or past posting_start
                const isUnlockedStage5 = currentStageIndex >= 4;
                
                // Stage 6 is unlocked if at least one posting is active (we're past posting_start)
                const isUnlockedStage6 = currentStageIndex >= 5;
                
                // Determine completion status
                let isCompleted = index < currentStageIndex;
                if (isStage5) isCompleted = isCompleteStage5;
                if (isStage6) isCompleted = isCompleteStage6;
                
                // Determine if locked
                let isLocked = index > currentStageIndex;
                if (isStage5) isLocked = !isUnlockedStage5;
                if (isStage6) isLocked = !isUnlockedStage6;
                
                // Determine if active
                let isActive = index === currentStageIndex;
                if (isStage5) isActive = isUnlockedStage5 && !isCompleteStage5 && !isPartialStage5;
                if (isStage6) isActive = isUnlockedStage6 && !isCompleteStage6;
                
                return (
                  <PipelineStage
                    key={stage.stage}
                    stage={stage.stage}
                    title={stage.title}
                    description={stage.description}
                    isCompleted={isCompleted}
                    isActive={isActive}
                    isLocked={isLocked}
                    isPartial={isPartialStage5}
                    partialText={isPartialStage5 ? `${postingStatus.count} de 2 plataformas` : undefined}
                    onClick={() => {
                      if (!isLocked) navigate(stage.route);
                    }}
                  />
                );
              })}
            </div>
          </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Action Cards Grid */}
        {/* Loja da Maison Card */}
        <a
          href="https://maisonalema.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <div 
            className="relative h-48 md:h-56 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
            style={{
              backgroundImage: `url(${lojaMaisonHero})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />
            
            {/* Text content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4">
              <h2 className="font-display text-3xl md:text-4xl tracking-widest drop-shadow-lg mb-2">
                Loja da Maison
              </h2>
              <p className="text-lg md:text-xl font-light tracking-wide drop-shadow-md opacity-90 mb-4">
                bikinis
              </p>
              <span className="inline-flex items-center gap-2 bg-white text-black px-5 py-2 rounded-full font-semibold text-sm uppercase tracking-wider shadow-lg group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                Ver a Loja
              </span>
            </div>
          </div>
        </a>

        {/* Profile Incomplete Dialog */}
        <ProfileIncompleteDialog 
          open={showProfileIncompleteDialog} 
          onOpenChange={setShowProfileIncompleteDialog}
          missingQuestions={isProfileComplete && !areQuestionsAnswered}
        />
      </div>
    </AppLayout>
  );
}
